import React from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { a11yDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import DescriptionListContainer from "../../common/components/descriptionlistcontainer";
import DescriptionListItem from "../../common/components/descriptionlistitem";
import Panel from "../../common/components/panel";
import { AgentFragment, Application } from "../../graphql/generated";

type AgentWithProfile = AgentFragment & {
  rdcProfileJson?: unknown;
};

const OverrideField: React.FC<{
  title: string;
  rdcValue?: string | null;
  dynamoValue?: string | null;
  info?: string;
}> = ({ title, rdcValue, dynamoValue, info }) => {
  if (!rdcValue) return null;

  const differs = dynamoValue && rdcValue !== dynamoValue;

  return (
    <DescriptionListContainer title={title} info={info}>
      <span>{rdcValue}</span>
      {differs && (
        <span className="block text-xs text-gray-400 line-through">
          {dynamoValue}
        </span>
      )}
    </DescriptionListContainer>
  );
};

const RdcProfile: React.FC<{ agent?: AgentWithProfile | AgentFragment }> = ({
  agent,
}) => {
  if (agent?.mls?.application !== Application.RealtorPlus) {
    return null;
  }

  const rawRdcProfile =
    "rdcProfileJson" in agent ? (agent as AgentWithProfile).rdcProfileJson : null;
  const rdcProfileJson = rawRdcProfile
    ? JSON.stringify(rawRdcProfile, null, 2)
    : null;
  const hasOverrides = !!(agent.rdcName || agent.rdcEmail || agent.rdcPhone);

  return (
    <Panel>
      <Panel.Title>RDC Profile</Panel.Title>
      <Panel.Body summary>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-3">
          <DescriptionListItem
            title="MLS Set ID"
            value={agent.rdcMlsSetId}
            info="The MLS set identifier used by the RDC ecosystem for this agent"
          />
          <OverrideField
            title="Name (RDC)"
            rdcValue={agent.rdcName}
            dynamoValue={agent.name}
            info="Agent name from the RDC backdoor profile system"
          />
          <OverrideField
            title="Email (RDC)"
            rdcValue={agent.rdcEmail}
            dynamoValue={agent.email}
            info="Agent email from the RDC backdoor profile system"
          />
          <OverrideField
            title="Phone (RDC)"
            rdcValue={agent.rdcPhone}
            dynamoValue={agent.phone}
            info="Agent phone from the RDC backdoor profile system"
          />
          {!hasOverrides && (
            <DescriptionListContainer
              title="Profile Source"
              info="Whether the agent's profile data comes from the RDC backdoor system or legacy DynamoDB"
            >
              Legacy DynamoDB
            </DescriptionListContainer>
          )}
        </dl>
      </Panel.Body>
      {rdcProfileJson && (
        <Panel.Body fullWidth constrainHeight>
          <SyntaxHighlighter language="json" style={a11yDark} showLineNumbers>
            {rdcProfileJson}
          </SyntaxHighlighter>
        </Panel.Body>
      )}
    </Panel>
  );
};

export default RdcProfile;
